import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import PrintPortal from '../components/PrintPortal';
import { inr, dt, apiError } from '../utils/format';
import { useAuth } from '../context/AuthContext';

const Metric = ({ label, value, strong, accent }) => (
  <div className="pnl-metric" style={{ border: '1px solid var(--border, #e5e0d5)', borderRadius: 8, padding: '.75rem 1rem', minWidth: 150 }}>
    <div className="muted small" style={{ textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
    <div style={{ fontSize: strong ? '1.4rem' : '1.15rem', fontWeight: 700, color: accent || 'inherit' }}>{inr(value)}</div>
  </div>
);

const Finance = () => {
  const { can } = useAuth();
  const canClose = can('finance:write');
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [pnl, setPnl] = useState(null);
  const [period, setPeriod] = useState(null);
  const [closed, setClosed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [periods, setPeriods] = useState([]);
  // A month with no period row was never opened — the UI offers to open it rather
  // than to reopen it, and a month that has not started yet cannot be closed at all.
  const [future, setFuture] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/finance/pnl?month=${month}`).then(({ data }) => {
      setPnl(data.pnl); setClosed(data.closed); setPeriod(data.period); setFuture(!!data.future);
    }).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [month]);
  useEffect(load, [load]);

  const loadPeriods = useCallback(() => {
    api.get('/finance/periods').then(({ data }) => setPeriods(data.periods || [])).catch(() => {});
  }, []);
  useEffect(loadPeriods, [loadPeriods]);

  const closeMonth = async () => {
    if (!window.confirm(`Close ${month}? This snapshots the P&L and locks the month — no sales, expenses or payroll can be back-dated into it until reopened.`)) return;
    setBusy(true);
    try { await api.post('/finance/periods/close', { month }); toast.success(`${month} closed`); load(); loadPeriods(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  // Also opens a month the system has never seen: months are closed by default, so
  // this is how an old month is unlocked for corrections.
  const openedBefore = !!period;
  const reopenMonth = async () => {
    const q = openedBefore
      ? `Reopen ${month}? The period lock is removed so entries can be edited again.`
      : `Open ${month}? Months are closed until opened — this lets sales, expenses and payroll be posted into ${month}.`;
    if (!window.confirm(q)) return;
    setBusy(true);
    try { await api.post('/finance/periods/reopen', { month }); toast.success(`${month} ${openedBefore ? 'reopened' : 'opened'}`); load(); loadPeriods(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const t = pnl?.totals;
  const byCat = pnl ? Object.entries(pnl.byCategory || {}).sort((a, b) => b[1] - a[1]) : [];

  // Built once, rendered twice: on screen, and into the print portal — the printer
  // only ever sees the portal, so the surrounding app adds no blank pages.
  const report = !pnl ? null : (
      <div className="pnl-report">
        <div className="print-head" style={{ marginBottom: '1rem' }}>
          <h2 style={{ margin: 0 }}>Profit &amp; Loss — {month}</h2>
          <div className="muted small">
            All branches · Revenue is net of GST, after returns
            {closed && period && <> · Closed {dt(period.closedAt)}</>}
          </div>
        </div>

        {/* Headline metrics */}
        <div className="flex gap" style={{ flexWrap: 'wrap', marginBottom: '1.25rem' }}>
          <Metric label="Revenue" value={t.revenue} />
          <Metric label="Cost of Goods" value={t.cogs} />
          <Metric label="Gross Profit" value={t.grossProfit} />
          <Metric label="Salaries" value={t.salaries} />
          <Metric label="Operating Exp." value={t.opex} />
          <Metric label="Net Profit" value={t.netProfit} strong accent={t.netProfit >= 0 ? '#2f6d1f' : '#c0392b'} />
        </div>

        {/* Consolidated statement */}
        <div className="table-wrap" style={{ maxWidth: 520, marginBottom: '1.5rem' }}>
          <table className="grid">
            <tbody>
              <tr><td>Revenue (net of GST, less returns)</td><td className="right">{inr(t.revenue)}</td></tr>
              <tr><td>Less: Cost of Goods Sold</td><td className="right">({inr(t.cogs)})</td></tr>
              <tr style={{ fontWeight: 700 }}><td>Gross Profit</td><td className="right">{inr(t.grossProfit)}</td></tr>
              <tr><td>Less: Salaries &amp; Wages</td><td className="right">({inr(t.salaries)})</td></tr>
              <tr><td>Less: Operating Expenses</td><td className="right">({inr(t.opex)})</td></tr>
              <tr style={{ fontWeight: 800, borderTop: '2px solid var(--border,#e5e0d5)' }}>
                <td>Net Profit</td>
                <td className="right" style={{ color: t.netProfit >= 0 ? '#2f6d1f' : '#c0392b' }}>{inr(t.netProfit)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Per-branch breakdown */}
        <h3 style={{ margin: '0 0 .5rem' }}>By Branch</h3>
        <div className="table-wrap" style={{ marginBottom: '1.5rem' }}>
          <table className="grid">
            <thead><tr><th>Branch</th><th className="right">Revenue</th><th className="right">COGS</th><th className="right">Gross</th><th className="right">Salaries</th><th className="right">Opex</th><th className="right">Net Profit</th></tr></thead>
            <tbody>
              {pnl.branches.map((b) => (
                <tr key={b.locationId == null ? 'central' : b.locationId}>
                  <td>{b.name}</td>
                  <td className="right">{inr(b.revenue)}</td>
                  <td className="right">{inr(b.cogs)}</td>
                  <td className="right">{inr(b.grossProfit)}</td>
                  <td className="right">{inr(b.salaries)}</td>
                  <td className="right">{inr(b.opex)}</td>
                  <td className="right" style={{ fontWeight: 700, color: b.netProfit >= 0 ? '#2f6d1f' : '#c0392b' }}>{inr(b.netProfit)}</td>
                </tr>
              ))}
              {!pnl.branches.length && <tr><td colSpan={7} className="muted">No activity this month.</td></tr>}
            </tbody>
            <tfoot><tr style={{ fontWeight: 700 }}><td>Total</td><td className="right">{inr(t.revenue)}</td><td className="right">{inr(t.cogs)}</td><td className="right">{inr(t.grossProfit)}</td><td className="right">{inr(t.salaries)}</td><td className="right">{inr(t.opex)}</td><td className="right">{inr(t.netProfit)}</td></tr></tfoot>
          </table>
        </div>

        {/* Expense category breakdown */}
        {byCat.length > 0 && (<>
          <h3 style={{ margin: '0 0 .5rem' }}>Operating Expenses by Category</h3>
          <div className="table-wrap" style={{ maxWidth: 420, marginBottom: '1.5rem' }}>
            <table className="grid">
              <tbody>
                {byCat.map(([cat, amt]) => <tr key={cat}><td>{cat}</td><td className="right">{inr(amt)}</td></tr>)}
                <tr style={{ fontWeight: 700 }}><td>Total Operating Expenses</td><td className="right">{inr(t.opex)}</td></tr>
              </tbody>
            </table>
          </div>
        </>)}
      </div>
  );

  return (
    <div>
      <div className="section-head no-print" style={{ flexWrap: 'wrap', gap: '.5rem' }}>
        <div className="flex gap" style={{ alignItems: 'center' }}>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
          {!closed
            ? <span className="badge" style={{ background: '#fdf0d8', color: '#8a6d1a', padding: '.25rem .6rem', borderRadius: 999, fontWeight: 700 }}>○ Open</span>
            : (future
              ? <span className="badge" style={{ background: '#eee', color: '#666', padding: '.25rem .6rem', borderRadius: 999, fontWeight: 700 }}>Not started</span>
              : (openedBefore
                ? <span className="badge" style={{ background: '#e8f3e0', color: '#2f6d1f', padding: '.25rem .6rem', borderRadius: 999, fontWeight: 700 }}>● Closed</span>
                : <span className="badge" style={{ background: '#eee', color: '#666', padding: '.25rem .6rem', borderRadius: 999, fontWeight: 700 }}>Not opened</span>))}
        </div>
        <div className="flex gap">
          <button className="btn btn-outline btn-sm" onClick={() => window.print()} disabled={!pnl}>Print</button>
          {canClose && !closed && !future && <button className="btn btn-primary" onClick={closeMonth} disabled={busy}>{busy ? 'Working…' : 'Close Month'}</button>}
          {canClose && closed && !future && <button className="btn btn-outline" onClick={reopenMonth} disabled={busy}>{busy ? 'Working…' : (openedBefore ? 'Reopen' : 'Open month')}</button>}
        </div>
      </div>

      {loading || !pnl ? <div className="spinner" /> : (
        <>
          {report}
          <PrintPortal>{report}</PrintPortal>
        </>
      )}

      {/* Closed periods history */}
      {periods.length > 0 && (
        <div className="no-print" style={{ marginTop: '2rem' }}>
          <h3 style={{ margin: '0 0 .5rem' }}>Closed Months</h3>
          <div className="table-wrap">
            <table className="grid">
              <thead><tr><th>Month</th><th>Status</th><th className="right">Revenue</th><th className="right">Net Profit</th><th>Closed</th><th></th></tr></thead>
              <tbody>
                {periods.map((p) => (
                  <tr key={p.month}>
                    <td>{p.month}</td>
                    <td>{p.status === 'closed' ? <span style={{ color: '#2f6d1f', fontWeight: 700 }}>Closed</span> : <span className="muted">Reopened</span>}</td>
                    <td className="right">{inr(p.revenue)}</td>
                    <td className="right" style={{ color: Number(p.netProfit) >= 0 ? '#2f6d1f' : '#c0392b' }}>{inr(p.netProfit)}</td>
                    <td className="muted small">{dt(p.closedAt)}</td>
                    <td className="right"><button className="btn btn-outline btn-sm" onClick={() => setMonth(p.month)}>View</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Finance;
